/**
 * Universal Text Generator - Glyph-Based Approach
 *
 * Uses opentype.js to extract glyph vector paths, providing:
 * - Exact measurements (vector math, not browser rendering)
 * - No font loading timing issues (font parsed directly)
 * - Works with any font (extracted from actual font file)
 * - Identical SVG/XCS output (same glyph data)
 *
 * Used by both svgGenerator.ts and xcsGenerator.ts
 */

import * as opentype from 'opentype.js';
import { ROBOTO_REGULAR_BASE64 } from '../utils/robotoFont';

// Font interface (matches context)
export interface Font {
    id: string;
    name: string;
    family: string;
    url: string;
}

// Bounding box interface
export interface BBox {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

// Box (target area) interface
export interface Box {
    x: number;
    y: number;
    width: number;
    height: number;
}

// Text path result
export interface TextPathResult {
    path: string;      // SVG path d attribute
    bounds: BBox;      // Bounding box of the path
}

// Transform result for positioning
export interface TransformResult {
    scale: number;
    translateX: number;
    translateY: number;
}

// Full text generation result
export interface GeneratedTextPath {
    path: string;           // SVG path d attribute
    transform: string;      // SVG transform attribute
    fill: string;          // Fill color (or 'none')
    stroke: string;        // Stroke color (or 'none')
    strokeWidth?: number;  // Stroke width if stroked
}

// Font cache to avoid reloading
const fontCache: Map<string, opentype.Font> = new Map();

/**
 * Load a font from base64 data or URL using opentype.js
 * Supports both base64-encoded font data (Tauri/desktop) and HTTP URLs (SaaS/Supabase Storage)
 * Caches fonts to avoid repeated parsing/fetching
 */
export async function loadFont(fontData: string): Promise<opentype.Font> {
    // Check cache first
    if (fontCache.has(fontData)) {
        return fontCache.get(fontData)!;
    }

    let buffer: ArrayBuffer;

    if (fontData.startsWith('http://') || fontData.startsWith('https://')) {
        // URL path (SaaS): fetch the font file
        const response = await fetch(fontData);
        if (!response.ok) {
            throw new Error(`Failed to fetch font: ${response.status} ${response.statusText}`);
        }
        buffer = await response.arrayBuffer();
    } else {
        // Base64 path (Tauri): decode from base64
        const base64Data = fontData.includes(',') ? fontData.split(',')[1] : fontData;
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        buffer = bytes.buffer;
    }

    const font = opentype.parse(buffer);

    // Cache the font
    fontCache.set(fontData, font);

    return font;
}

/**
 * Convert opentype.js path to SVG path string
 */
export function pathToSvg(path: opentype.Path): string {
    const commands: string[] = [];

    for (const cmd of path.commands) {
        switch (cmd.type) {
            case 'M':
                commands.push(`M${cmd.x} ${cmd.y}`);
                break;
            case 'L':
                commands.push(`L${cmd.x} ${cmd.y}`);
                break;
            case 'C':
                commands.push(`C${cmd.x1} ${cmd.y1} ${cmd.x2} ${cmd.y2} ${cmd.x} ${cmd.y}`);
                break;
            case 'Q':
                commands.push(`Q${cmd.x1} ${cmd.y1} ${cmd.x} ${cmd.y}`);
                break;
            case 'Z':
                commands.push('Z');
                break;
        }
    }

    return commands.join('');
}

/**
 * Get text as an SVG path with its bounding box
 * Uses a reference size of 72 units (standard for fonts)
 */
export function getTextPath(font: opentype.Font, text: string, refSize: number = 72): TextPathResult {
    // Get path for entire text string at origin
    // Note: opentype.js Y coordinates are inverted (positive Y is up)
    // We render at Y=0 (baseline) and handle positioning in transform
    const path = font.getPath(text, 0, 0, refSize);
    const pathStr = pathToSvg(path);
    const bbox = path.getBoundingBox();

    return {
        path: pathStr,
        bounds: {
            minX: bbox.x1,
            minY: bbox.y1,
            maxX: bbox.x2,
            maxY: bbox.y2
        }
    };
}

/**
 * Calculate scale and position to fit text path in target box
 * - Text fills the box as much as possible
 * - Vertically centered
 * - Horizontally centered
 * - Never exceeds box boundaries
 */
export function calculateGlyphFit(bounds: BBox, box: Box): TransformResult {
    const textWidth = bounds.maxX - bounds.minX;
    const textHeight = bounds.maxY - bounds.minY;

    // Handle empty text (avoid division by zero)
    if (textWidth <= 0 || textHeight <= 0) {
        return {
            scale: 1,
            translateX: box.x,
            translateY: box.y
        };
    }

    // Scale to fit box (use smaller scale to fit both dimensions)
    // Apply 99.6% buffer (0.4% padding) to prevent edge clipping
    const scaleX = (box.width * 0.996) / textWidth;
    const scaleY = (box.height * 0.996) / textHeight;
    const scale = Math.min(scaleX, scaleY);

    // Calculate scaled width for horizontal centering
    const scaledWidth = textWidth * scale;

    // Horizontal centering: box center - half of scaled width, adjusted for minX offset
    const translateX = box.x + (box.width - scaledWidth) / 2 - bounds.minX * scale;

    // Vertical centering in SVG coordinates:
    // opentype.js getPath() returns paths in SVG coordinate system (Y increases downward)
    // When rendered at baseline y=0:
    // - bounds.minY = top of text (negative, e.g., -50 for ascenders)
    // - bounds.maxY = bottom of text (near 0 or positive for descenders)
    //
    // SVG transform: translate(tx, ty) scale(s) applies as:
    //   final_y = original_y * scale + translateY
    //
    // For vertical centering, we want the midpoint of the text at the box midpoint:
    //   (bounds.minY + bounds.maxY) / 2 * scale + translateY = box.y + box.height / 2
    //   translateY = box.y + box.height / 2 - (bounds.minY + bounds.maxY) / 2 * scale
    const translateY = box.y + box.height / 2 - (bounds.minY + bounds.maxY) / 2 * scale;

    return { scale, translateX, translateY };
}

/**
 * Find a font by name from the fonts array
 * Handles various naming conventions
 */
export function findFontByName(fonts: Font[], fontFamily: string): Font | undefined {
    const normalizedSearch = fontFamily.toLowerCase().replace(/[-_\s]/g, '');

    return fonts.find(f => {
        const normalizedName = f.name.toLowerCase().replace(/[-_\s]/g, '');
        const normalizedFamily = f.family.toLowerCase().replace(/[-_\s]/g, '');
        return normalizedName.includes(normalizedSearch) ||
               normalizedSearch.includes(normalizedName) ||
               normalizedFamily.includes(normalizedSearch) ||
               normalizedSearch.includes(normalizedFamily);
    });
}

/**
 * Main entry point: Generate text as an SVG path with transform
 *
 * @param text - The text to render
 * @param fontFamily - Font family name (must exist in fonts array)
 * @param fonts - Array of available fonts with base64 data
 * @param box - Target bounding box to fit text into
 * @returns Path data and transform, or null if font not found
 */
export async function generateTextPath(
    text: string,
    fontFamily: string,
    fonts: Font[],
    box: Box
): Promise<{ path: string; transform: string } | null> {
    // Find font by name
    const fontData = findFontByName(fonts, fontFamily);

    try {
        // Load font — use matched font or fall back to bundled Roboto
        let font: opentype.Font;
        if (fontData?.url) {
            font = await loadFont(fontData.url);
        } else {
            if (fontFamily.toLowerCase() !== 'roboto') {
                console.warn(`[TextGenerator] Font "${fontFamily}" not in fonts list, using bundled Roboto`);
            }
            font = await loadFont(ROBOTO_REGULAR_BASE64);
        }

        // Get text path and bounds
        const { path, bounds } = getTextPath(font, text);

        // Calculate transform to fit in box
        const { scale, translateX, translateY } = calculateGlyphFit(bounds, box);

        // Build transform string
        const transform = `translate(${translateX}, ${translateY}) scale(${scale})`;

        return { path, transform };
    } catch (error) {
        console.error(`[TextGenerator] Error generating text path:`, error);
        return null;
    }
}

/**
 * Generate text path with full styling options
 * Supports score (stroke), engrave (fill), and cut (dashed stroke)
 */
export async function generateStyledTextPath(
    text: string,
    fontFamily: string,
    fonts: Font[],
    box: Box,
    options: {
        score?: boolean;
        engrave?: boolean;
        cut?: boolean;
        scoreColor?: string;
        engraveColor?: string;
        cutColor?: string;
    }
): Promise<GeneratedTextPath[] | null> {
    const result = await generateTextPath(text, fontFamily, fonts, box);

    if (!result) {
        return null;
    }

    const paths: GeneratedTextPath[] = [];
    const { path, transform } = result;

    // Default colors
    const scoreColor = options.scoreColor || '#fe0002';    // xTool Red
    const engraveColor = options.engraveColor || '#000000'; // xTool Black
    const cutColor = options.cutColor || '#00befe';         // xTool Cyan

    // Score: stroke, no fill
    if (options.score) {
        paths.push({
            path,
            transform,
            fill: 'none',
            stroke: scoreColor,
            strokeWidth: 0.5
        });
    }

    // Engrave: fill, no stroke (default if nothing specified)
    if (options.engrave !== false || (!options.score && !options.cut)) {
        paths.push({
            path,
            transform,
            fill: engraveColor,
            stroke: 'none'
        });
    }

    // Cut: dashed stroke, no fill
    if (options.cut) {
        paths.push({
            path,
            transform,
            fill: 'none',
            stroke: cutColor,
            strokeWidth: 0.5
        });
    }

    return paths;
}

/**
 * Calculate the optimal fontSize to fit text in a bounding box
 *
 * Used by xcsGenerator to create TEXT shapes with accurate font sizing.
 * Unlike generateTextPath() which outputs PATH elements with transforms,
 * this returns a fontSize value for use with TEXT elements (editable in xTool).
 *
 * @param text - The text to measure
 * @param fontFamily - Font family name
 * @param fonts - Array of available fonts with base64 data
 * @param box - Target bounding box to fit text into
 * @param fallbackFontData - Optional fallback font data (base64) if fontFamily not found
 * @returns fontSize that will make the text fit the box
 */
export async function calculateFontSizeForBox(
    text: string,
    fontFamily: string,
    fonts: Font[],
    box: Box,
    fallbackFontData?: string
): Promise<number> {
    // Find font by name
    const fontData = findFontByName(fonts, fontFamily);

    let font: opentype.Font;

    try {
        if (fontData?.url) {
            font = await loadFont(fontData.url);
        } else if (fallbackFontData) {
            font = await loadFont(fallbackFontData);
        } else {
            console.warn(`[TextGenerator] Font "${fontFamily}" not found, using fallback fontSize`);
            return 12; // Safe fallback
        }
    } catch (error) {
        console.error(`[TextGenerator] Error loading font for size calculation:`, error);
        return 12; // Safe fallback
    }

    // Measure text at reference size (72 units - standard font reference)
    const path = font.getPath(text, 0, 0, 72);
    const bounds = path.getBoundingBox();

    const textWidth = bounds.x2 - bounds.x1;
    const textHeight = bounds.y2 - bounds.y1;

    // Handle empty text or zero dimensions
    if (textWidth <= 0 || textHeight <= 0) {
        return 12; // Safe fallback for empty/whitespace text
    }

    // Scale to fit box (99.6% buffer = 0.4% padding to prevent edge clipping)
    const scaleX = (box.width * 0.996) / textWidth;
    const scaleY = (box.height * 0.996) / textHeight;
    const scale = Math.min(scaleX, scaleY);

    // Final fontSize = reference size * scale
    // This is the fontSize that makes text fit perfectly in the box
    return 72 * scale;
}

/**
 * Text fit info result - includes both fontSize and scaled bounds
 */
export interface TextFitInfo {
    fontSize: number;
    scaledBounds: {
        minY: number;  // Top of text at fontSize (negative for ascenders)
        maxY: number;  // Bottom of text at fontSize (positive for descenders)
    };
}

/**
 * Calculate fontSize AND scaled bounds for positioning text in a bounding box
 *
 * Used by lbrnGenerator to create TEXT shapes with accurate positioning.
 * Returns both the fontSize (for text sizing) and scaled bounds (for positioning).
 * This enables bottom-aligned text matching the app preview.
 *
 * @param text - The text to measure
 * @param fontFamily - Font family name
 * @param fonts - Array of available fonts with base64 data
 * @param box - Target bounding box to fit text into
 * @param fallbackFontData - Optional fallback font data (base64) if fontFamily not found
 * @returns TextFitInfo with fontSize and scaledBounds
 */
export async function calculateTextFitInfo(
    text: string,
    fontFamily: string,
    fonts: Font[],
    box: Box,
    fallbackFontData?: string
): Promise<TextFitInfo> {
    // Find font by name
    const fontData = findFontByName(fonts, fontFamily);

    let font: opentype.Font;

    try {
        if (fontData?.url) {
            font = await loadFont(fontData.url);
        } else if (fallbackFontData) {
            font = await loadFont(fallbackFontData);
        } else {
            console.warn(`[TextGenerator] Font "${fontFamily}" not found, using fallback fontSize`);
            return { fontSize: 12, scaledBounds: { minY: -10, maxY: 2 } }; // Safe fallback
        }
    } catch (error) {
        console.error(`[TextGenerator] Error loading font for text fit calculation:`, error);
        return { fontSize: 12, scaledBounds: { minY: -10, maxY: 2 } }; // Safe fallback
    }

    // Measure text at reference size (72 units - standard font reference)
    const path = font.getPath(text, 0, 0, 72);
    const bounds = path.getBoundingBox();

    const textWidth = bounds.x2 - bounds.x1;
    const textHeight = bounds.y2 - bounds.y1;

    // Handle empty text or zero dimensions
    if (textWidth <= 0 || textHeight <= 0) {
        return { fontSize: 12, scaledBounds: { minY: -10, maxY: 2 } }; // Safe fallback
    }

    // Scale to fit box (99% buffer to prevent edge clipping)
    const scaleX = (box.width * 0.99) / textWidth;
    const scaleY = (box.height * 0.99) / textHeight;
    const scale = Math.min(scaleX, scaleY);

    // Return fontSize and scaled bounds
    // bounds.y1 = top of text (negative for ascenders above baseline)
    // bounds.y2 = bottom of text (positive for descenders below baseline)
    return {
        fontSize: 72 * scale,
        scaledBounds: {
            minY: bounds.y1 * scale,
            maxY: bounds.y2 * scale,
        }
    };
}

/**
 * Clear the font cache
 * Useful when fonts are updated
 */
export function clearFontCache(): void {
    fontCache.clear();
}
